import { any, compose, curryN, isNil, pathOr } from 'ramda';
import React, { Component } from 'react';
import DocumentTitle from 'react-document-title';
import IdleTimer from 'react-idle-timer';
import { injectIntl } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { connect } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { GlobalStyles } from '@fortress-technology-solutions/fortress-component-library/design';
import ExtendSessionModal from '../../components/ExtendSessionModal';
import MissingChargesNotice from '../../components/MissingChargesNotice';
import WalkmeHiddenFields from '../../components/WalkmeHiddenFields';
import ErrorBoundary from '../../containers/ErrorBoundary';
import { HomeTabs } from '../Home';
import { setActiveKey } from '../Home/actions';
import { isUserActive, isUserIdle, startTokenRenewal } from '../Login/actions';
import { extendUserSession } from './actions';
import { getAllKpis } from '../Overview/actions';
import * as appActions from './actions';
import { AppContext } from './context';
import { isUserLoggedIn, getEnvironmentName } from './selectors';
import type {
  Actions,
  BuildInformation,
  CustomLDProp,
  GlobalState,
  Property,
  UserPermission,
} from './types';
import urlsUserIdleBlackList from './urlsUserIdleBlackList';

import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import queryClient from '../../react-query-client';
import MaintenancePage from '../Maintenance';
import AccountSuspendedPage from '../AccountSuspended';
import Routes from './Routes';
import ScrollToTop from './ScrollToTop';
import { getCookie } from '../../utils/cookie';
import {
  updateActivityTimestamp,
  setupCrossTabActivityListener,
  broadcastSessionExtended,
  getLatestActivityTimestamp,
  getLocalLoginTimestamp,
  recordLocalLoginTimestamp,
} from '../../utils/crossTabActivity';
import { hideExtendUserSessionModal } from './actions';

type Props = {
  buildInformation: BuildInformation,
  selectedProperty?: Property,
  userProperties: Array<Property>,
  userPermissions: Array<UserPermission>,
  showExtendUserSessionModal: boolean,
  extendUserSessionModalTimestamp: ?number,
  isUser: boolean,
  selectedPropertyClassId: string,
  userRoleId: string,
  username: string,
  userId: string,
  organizationId: string,
  environmentName: string,
  flags: {
    maintenanceMode: boolean,
    accountSuspended: boolean,
  },
};
type InjectedProps = {
  intl: any,
  history: Object,
  actions: Actions,
  location: any,
};

type State = {
  abortController: Object,
};

export class App extends Component<Props & InjectedProps, State> {
  idleTimer: ?Object;
  removeListenerFunction: Function;
  unlisten: Function;
  crossTabListenerCleanup: ?Function;
  activityEventListeners: Array<{ event: string, handler: Function }>;
  visibilityChangeHandler: ?Function;

  constructor(props: Props & InjectedProps) {
    super(props);

    // $FlowFixMe
    const abortController = new AbortController(); // eslint-disable-line
    this.state = {
      abortController,
    };
    this.idleTimer = React.createRef();
    this.removeListenerFunction = () => {};
    this.crossTabListenerCleanup = null;
    this.activityEventListeners = [];
  }

  isToken = () => {
    const currentToken = getCookie('session_id');
    return !isNil(currentToken);
  };
  currentUrlisBlackListed = () => {
    const currentUrl = this.props.location.pathname;
    return !any((url) => url === currentUrl, urlsUserIdleBlackList);
  };
  getIdleTimerTimeout = () =>
    60000 *
    Number(import.meta.env.REACT_APP_IDLE_TIMER_TIMEOUT_MINUTES ?? '30'); // Increased from 5 to 30 minutes

  getPropertyIdFromPathname(pathname: ?string): ?string {
    // Routes are shaped like /property/:propertyId/...
    return pathname?.split('/')?.[2];
  }

  selectPropertyIfParams(location) {
    // Have to do it this way instead of match because of the way our router is structured
    // React-Router doesn't let you get the path params from a global level, only in the component that
    // is defined on the route, first variable is property id
    const propertyId = this.getPropertyIdFromPathname(location.pathname);
    if (propertyId) {
      const property = this.props.userProperties.find(
        (userProperty) => userProperty.id === propertyId,
      );
      if (property) {
        this.props.actions.selectProperty(property);
        this.props.actions.getSelectedPropertySubjournals();
        this.props.actions.getPropertyPaymentProvider();
        this.props.actions.getSelectedPropertyTransactionCodes();
      }
    }
  }

  componentDidMount() {
    this.props.actions.getAllNavigationOptions();
    this.updateLDClientContext({}, this.props);
    compose(this.startTokenRenewal, this.currentUrlisBlackListed)();
    this.selectPropertyIfParams(window.location);
    this.unlisten = this.props.history.listen((location) => {
      const propertyId = this.getPropertyIdFromPathname(location.pathname);
      if (this.props?.selectedProperty?.id !== propertyId) {
        this.selectPropertyIfParams(location);
      }
    });

    // Set up cross-tab activity tracking
    this.setupCrossTabActivityTracking();

    // Pause/resume idle detection based on tab visibility
    this.visibilityChangeHandler = this.handleVisibilityChange;
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Some flows (e.g. the legacy secondary lease application form) leave
    // this SPA via a full window.location navigation and return via the
    // browser's Back button. When the browser restores the previous page
    // from its back/forward cache instead of re-running the app from
    // scratch, every React Query cache is exactly as stale as it was the
    // moment the user navigated away — none of it ever re-fetches on its
    // own. event.persisted is how the browser tells us that happened.
    window.addEventListener('pageshow', this.handlePageShow);
  }

  componentWillUnmount() {
    this.unlisten();

    window.removeEventListener('pageshow', this.handlePageShow);

    // Remove visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
      this.visibilityChangeHandler = null;
    }

    // Clean up cross-tab activity listener
    try {
      if (this.crossTabListenerCleanup) {
        this.crossTabListenerCleanup();
        this.crossTabListenerCleanup = null;
      }
      // Remove all activity event listeners
      this.activityEventListeners.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler);
      });
      this.activityEventListeners = [];
    } catch (error) {
      // Ignore cleanup errors
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Error cleaning up cross-tab listener:', error);
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.selectedProperty && this.props.selectedProperty) {
      this.props.actions.getSelectedPropertySubjournals();
      this.props.actions.getPropertyPaymentProvider();
      this.props.actions.getSelectedPropertyTransactionCodes();
    }
    this.updateLDClientContext(prevProps, this.props);

    if (!prevProps.isUser && this.props.isUser) {
      // When the user authenticates (or re-authenticates after idle logout on the
      // same tab), explicitly restart the idle timer. With stopOnIdle={true} the
      // timer stops permanently when onIdle fires — it will not restart on its own
      // until a cross-tab activity storage event is received (e.g. opening another
      // tab). This reset closes that gap so idle detection works immediately after
      // every login, including re-login on the same tab.
      if (this.idleTimer?.current?.reset) {
        this.idleTimer.current.reset();
      }

      // Set up cross-tab tracking once per login. The existing listeners remain
      // bound to `this` and continue to work correctly after re-authentication,
      // so we only initialize them when the cleanup handle is not already set.
      if (!this.crossTabListenerCleanup) {
        this.setupCrossTabActivityTracking();
      }
    }
  }

  updateLDClientContext(prevProps: Object, props: Object) {
    const currentUsername = props?.username;
    const currentOrganizationId = props?.organizationId;
    const currentSelectedProperty = props?.selectedProperty;

    const previousUsername = prevProps?.username;
    const previousOrganizationId = prevProps?.organizationId;
    const prevSelectedProperty = prevProps?.selectedProperty;

    const usernameChanged = currentUsername !== previousUsername;
    const organizationIdChanged =
      currentOrganizationId !== previousOrganizationId;
    const selectedPropertyChanged =
      currentSelectedProperty?.id !== prevSelectedProperty?.id;

    const shouldUpdateLdProps = [
      usernameChanged,
      organizationIdChanged,
      selectedPropertyChanged,
    ].includes(true);

    if (shouldUpdateLdProps === false) return;

    this.updateCustomPropertiesForLd({
      username: currentUsername,
      organizationId: currentOrganizationId,
      propertyId: currentSelectedProperty?.id,
      physicalState: currentSelectedProperty?.physicalState?.replace?.(
        /US-/g,
        '',
      ),
      propertyType: currentSelectedProperty?.propertyClass?.name,
    });
  }

  updateCustomPropertiesForLd(ldCustomProps: CustomLDProp) {
    const { ldClient, userId } = this.props;

    // LaunchDarkly requires a non-empty key in the context object.
    // userId is '' when no user is authenticated (e.g. the login page).
    // Calling identify with an empty key throws "Invalid context specified."
    // Skip the call in that case — the anonymous 'fortressUser' context set
    // during initialisation in index.js remains active until the user logs in.
    if (!userId) return;

    ldClient.identify(
      {
        kind: 'user',
        key: userId,
        hostname: window.location.hostname, // Preserve hostname for feature flag targeting
        ...ldCustomProps,
      },
      null,
      () => {
        if (this.state.ldKey !== userId) {
          this.setState({ ldKey: userId });
        }
      },
    );
  }

  handlePageShow = (event: Event) => {
    if (event.persisted) {
      queryClient.invalidateQueries();
    }
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      // Tab going to background — pause idle timer so it never fires while hidden
      if (this.idleTimer?.current?.pause) {
        this.idleTimer.current.pause();
      }
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.log('[IdleTimer] Tab hidden — pausing idle timer');
      }
    } else {
      // Tab returning to foreground — check how long since last real activity
      const lastActivity = getLatestActivityTimestamp();
      const timeSinceLastActivity = lastActivity
        ? Date.now() - lastActivity
        : Infinity;
      const idleTimeout = this.getIdleTimerTimeout();

      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          `Tab visible: ${Math.round(
            timeSinceLastActivity / 1000,
          )}s since last activity` + ` (timeout: ${idleTimeout / 1000}s)`,
        );
      }

      if (timeSinceLastActivity >= idleTimeout) {
        // Idle threshold exceeded while away — trigger idle flow immediately
        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('[IdleTimer] Idle threshold exceeded on tab focus');
        }
        compose(this.isUserIdle, this.currentUrlisBlackListed)();
      } else {
        // Recent activity — resume idle timer from the beginning so the user
        // gets a full idle window from now
        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('[IdleTimer] Tab visible — resetting idle timer');
        }
        if (this.idleTimer?.current?.reset) {
          this.idleTimer.current.reset();
        }
      }
    }
  };

  startTokenRenewal = (urlIsAllowed: boolean) => {
    if (urlIsAllowed && this.props.isUser && this.isToken())
      this.props.actions.startTokenRenewal();
  };

  dismiss = () => {
    // Don't allow dismissing - user must acknowledge logout or extend
  };

  extendSession = () => {
    this.props.actions.extendUserSession();
    // Notify other tabs to close their session-expiry modals.
    // Pass userId so tabs with a different logged-in user ignore the event.
    broadcastSessionExtended(this.props.userId);
    // Restart the idle timer to ensure continuous idle detection
    // Without this, the timer stays stopped until the user interacts with the page
    if (this.idleTimer?.current?.reset) {
      this.idleTimer.current.reset();
    }
  };
  userIdle = () => {
    compose(this.isUserIdle, this.currentUrlisBlackListed)();
  };
  isUserIdle = (urlIsAllowed: boolean) => {
    if (urlIsAllowed && !this.props.showExtendUserSessionModal) {
      // Only show the modal in the visible/active tab. If this tab is in the
      // background when the timer fires, reset it and wait — the user may still
      // be active in another tab, and the cross-tab activity listener will keep
      // resetting the timer as long as that's the case.
      if (document.hidden) {
        if (this.idleTimer?.current?.reset) {
          this.idleTimer.current.reset();
        }
        return;
      }
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          '[IdleTimer] User is IDLE',
          new Date().toLocaleTimeString(),
        );
      }
      this.props.actions.isUserIdle();
    }
  };

  showExtendUserSessionModal = (urlIsAllowed: boolean) => {
    if (urlIsAllowed && !this.props.showExtendUserSessionModal)
      this.props.actions.isUserActive();
  };
  userActive = () => {
    // Update activity timestamp for cross-tab tracking
    updateActivityTimestamp();
    // Debug logging
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log('[IdleTimer] User active', new Date().toLocaleTimeString());
    }
    compose(this.showExtendUserSessionModal, this.currentUrlisBlackListed)();
  };

  setupCrossTabActivityTracking = () => {
    try {
      // Update activity timestamp on mount
      updateActivityTimestamp();

      // Handle session extension from another tab
      const handleSessionExtended = ({ userId }) => {
        // Ignore events from a different logged-in user (same browser profile,
        // two separate Fortress accounts open at once)
        if (userId !== this.props.userId) return;
        // Session was extended in another tab - close modal, cancel logout saga,
        // and reset idle timer so this tab behaves as if the user just acted
        this.props.actions.hideExtendUserSessionModal();
        this.props.actions.isUserActive();
        if (this.idleTimer?.current?.reset) {
          this.idleTimer.current.reset();
        }
      };

      // When another tab has activity, reset this tab's idle timer ONLY if
      // this tab is currently visible. Background tabs are paused via the
      // visibilitychange handler and should not be reset here — doing so
      // prevented them from ever going idle.
      const handleCrossTabActivity = () => {
        if (!document.hidden && this.idleTimer?.current?.reset) {
          this.idleTimer.current.reset();
        }
      };

      // When another tab logs out for this user, log out here too.
      const handleCrossTabLogout = ({ userId, timestamp }) => {
        if (userId !== this.props.userId) return;
        // Guard: ignore logout broadcasts whose emission timestamp predates the
        // most recent login on THIS tab, such as a delayed or queued message
        // from an earlier session. This compares the broadcast emission time to
        // the local login time; it does not detect whether an older idle timer
        // started before re-login and fired later (that case is handled by
        // handleCrossTabLogin cancelling the countdown on the broadcasting tab).
        const loginTs = getLocalLoginTimestamp();
        if (loginTs && timestamp && timestamp < loginTs) {
          if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line no-console
            console.log(
              '[crossTab] Ignoring stale logout broadcast (predates local login)',
              { logoutTimestamp: timestamp, loginTimestamp: loginTs },
            );
          }
          return;
        }
        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('[crossTab] Logout broadcast detected');
        }
        // Call logOut with fromBroadcast=true to prevent this tab re-broadcasting an echo
        this.props.actions.logOut(true);
        queryClient.clear();
      };

      // When another tab completes a fresh login for the same user, cancel any
      // stale idle-logout countdowns still running on this tab.
      // This is the primary fix for the "re-login then immediately booted" edge case:
      // background tabs may still have a logOutUserIdle saga counting down from the
      // previous session — this resets them so they don't broadcast logout after
      // the user has already re-authenticated in the active tab.
      const handleCrossTabLogin = ({ userId, timestamp }) => {
        // Ignore logins from a different user (two accounts open in same browser profile).
        if (userId !== this.props.userId) return;
        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('[crossTab] Login broadcast detected from another tab', {
            userId,
          });
        }
        // Sync this tab's login timestamp to the broadcast time so the stale-logout
        // guard in handleCrossTabLogout reflects the actual re-login moment rather
        // than whenever this tab happened to receive the storage event.
        recordLocalLoginTimestamp(timestamp);
        // Dispatch USER_IS_ACTIVE to cancel any running logOutUserIdle saga,
        // hide the expired-session modal if still showing, and reset the idle timer.
        this.props.actions.hideExtendUserSessionModal();
        this.props.actions.isUserActive();
        if (this.idleTimer?.current?.reset) {
          this.idleTimer.current.reset();
        }
      };

      // Listen for activity, session-extended, logout, and login events from other tabs.
      this.crossTabListenerCleanup = setupCrossTabActivityListener(
        handleCrossTabActivity,
        handleSessionExtended,
        handleCrossTabLogout,
        handleCrossTabLogin,
      );

      // Throttle activity timestamp updates to avoid excessive localStorage writes
      let lastUpdateTime = 0;
      const THROTTLE_MS = 1000; // Update at most once per second

      const throttledUpdateActivity = (event) => {
        const now = Date.now();
        if (now - lastUpdateTime >= THROTTLE_MS) {
          lastUpdateTime = now;
          updateActivityTimestamp();
          // Debug logging
          if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line no-console
            console.log(
              'Activity detected:',
              event?.type,
              new Date().toLocaleTimeString(),
            );
          }
        }
      };

      // Update activity timestamp on user interactions
      // Only track intentional actions - excludes passive events like mousemove/scroll
      const activityEvents = ['mousedown', 'keydown', 'touchstart', 'click'];

      activityEvents.forEach((event) => {
        document.addEventListener(event, throttledUpdateActivity, {
          passive: true,
        });
        this.activityEventListeners.push({
          event,
          handler: throttledUpdateActivity,
        });
      });
    } catch (error) {
      // If cross-tab tracking fails, log but don't break the app
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Failed to set up cross-tab activity tracking:', error);
      }
    }
  };

  logOut = () => {
    this.props.actions.logOut();
    // Clear queries because user is logging out to prevent other users from seeing cached data
    queryClient.clear();
  };

  onSelectProperty = (property: Property) => {
    this.props.history.push(`/property/${property.id}`);
    this.props.actions.setActiveKey(HomeTabs.OVERVIEW);
    const abortController = new AbortController(); // eslint-disable-line
    this.setState({ abortController }, () =>
      this.props.actions.getAllKpis(abortController.signal),
    );
  };

  onSelectPortfolioSummary = () => {
    this.props.history.push('/');
    if (this.props.history.location.pathname === '/') {
      //Remove the route change listener
      this.removeListenerFunction();
      this.props.actions.selectPortfolioSummary();
    } else {
      //If Prompt was fired, listen for route change before selecting a new property
      this.removeListenerFunction = this.props.history.listen(
        (location, action) => {
          this.props.actions.selectPortfolioSummary();
        },
      );
    }
  };

  render() {
    const onSelectPropertyCurried = curryN(2, this.onSelectProperty);
    const hasCommercialFloorPlans = this.props.selectedProperty
      ? this.props.selectedProperty.hasCommercialFloorPlans
      : '';

    // FF for the Transaction Batches route

    const { flags = {} } = this.props;
    const { maintenanceMode: enableMaintenanceMode = false } = flags;
    const isAccountSuspended = flags.accountSuspended ?? false;

    return (
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <AppContext.Provider
          value={{
            history: this.props.history,
            permissions: this.props.userPermissions,
            selectedProperty: this.props.selectedProperty,
            intl: this.props.intl,
            formatMessage: this.props.intl.formatMessage,
            onSelectProperty: onSelectPropertyCurried,
            onSelectPortfolioSummary: this.onSelectPortfolioSummary,
            logOut: this.logOut,
            userOrganizationId: this.props.organizationId,
            userId: this.props.userId,
            ldKey: this.state.ldKey,
            isLdUserContextReady: this.state.ldKey === this.props.userId,
          }}
        >
          <GlobalStyles />
          {enableMaintenanceMode ? (
            <MaintenancePage />
          ) : isAccountSuspended && this.props.isUser ? (
            <AccountSuspendedPage
              selectedProperty={this.props.selectedProperty}
              userProperties={this.props.userProperties}
              onSelectProperty={onSelectPropertyCurried}
              onSelectPortfolioSummary={this.onSelectPortfolioSummary}
              logOut={this.logOut}
              environmentName={this.props.environmentName}
            />
          ) : (
            <ErrorBoundary intl={this.props.intl}>
              <DocumentTitle title="Fortress">
                <div>
                  <WalkmeHiddenFields
                    userRoleId={this.props.userRoleId}
                    propertyClassId={this.props.selectedPropertyClassId}
                    username={this.props.username}
                    organizationId={this.props.organizationId}
                    hasCommercialFloorPlans={hasCommercialFloorPlans}
                  />
                  {this.props.showExtendUserSessionModal && (
                    <ExtendSessionModal
                      dismiss={this.dismiss}
                      extendSession={this.extendSession}
                      userIdle={() => {}}
                      logOut={this.logOut}
                      showExtendUserSessionModal={
                        this.props.showExtendUserSessionModal
                      }
                      modalTimestamp={
                        this.props.extendUserSessionModalTimestamp
                      }
                      intl={this.props.intl}
                    />
                  )}
                  <MissingChargesNotice />
                  <ReduxToastr
                    transitionIn="fadeIn"
                    transitionOut="fadeOut"
                    progressBar
                  />
                  <IdleTimer
                    id="IdleTimer"
                    ref={this.idleTimer}
                    onActive={this.userActive}
                    onIdle={this.userIdle}
                    timeout={this.getIdleTimerTimeout()}
                    events={['keydown', 'mousedown', 'touchstart', 'click']}
                    debounce={250}
                    stopOnIdle={true}
                  >
                    <ScrollToTop />
                    <Routes />
                  </IdleTimer>
                </div>
              </DocumentTitle>
            </ErrorBoundary>
          )}
        </AppContext.Provider>
      </QueryClientProvider>
    );
  }
}

export const mapStateToProps = (globalState: GlobalState): Props => {
  const { app } = globalState;
  return {
    buildInformation: app.buildInformation,
    selectedProperty: app.selectedProperty,
    // $FlowFixMe
    selectedPropertyClassId: pathOr(
      'portfolioSummary',
      ['selectedProperty', 'propertyClass', 'id'],
      app,
    ),
    userId: app?.currentUser?.user?.id ?? '',
    // $FlowFixMe
    userRoleId: pathOr('', ['currentUser', 'user', 'userRoleId'], app),
    // $FlowFixMe
    username: pathOr('', ['currentUser', 'user', 'username'], app),
    // $FlowFixMe
    organizationId: pathOr('', ['currentUser', 'user', 'organizationId'], app),
    // $FlowFixMe
    userProperties: pathOr([], ['currentUser', 'user', 'properties'], app),
    // $FlowFixMe
    userPermissions: pathOr([], ['currentUser', 'permissions'], app),
    showExtendUserSessionModal: app.showExtendUserSessionModal,
    extendUserSessionModalTimestamp: app.extendUserSessionModalTimestamp,
    isUser: isUserLoggedIn(globalState),
    environmentName: getEnvironmentName(globalState),
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      ...appActions,
      hideExtendUserSessionModal,
      getAllKpis,
      isUserActive,
      isUserIdle,
      setActiveKey,
      startTokenRenewal,
      extendUserSession,
    },
    dispatch,
  );
  return { actions };
}

const AppInjected = injectIntl(App);
const router = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AppInjected),
);
export default withLDConsumer()(router);
